import { useEffect, useState } from 'react';
import { supportService, messageService, SupportTicket } from '../services/api'; // Import messageService
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Clock, ExternalLink, MessageCircle, Send, X, Filter, SortAsc, SortDesc } from 'lucide-react'; // Import icons

const AdminSupportPage = () => {
    const { t } = useTranslation();
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filter and Sort State
    const [filterStatus, setFilterStatus] = useState<string>('ALL');
    const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

    // Message Modal State
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
    const [messageText, setMessageText] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const response = await supportService.getTickets();
            if (response.data.success) {
                setTickets(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch tickets', err);
            setError(t('support.admin.loadError', 'Failed to load tickets'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    // Apply Filter and Sort
    useEffect(() => {
        let result = [...tickets];

        // Filter
        if (filterStatus !== 'ALL') {
            result = result.filter(ticket => ticket.status === filterStatus);
        }

        // Sort
        result.sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

        setFilteredTickets(result);
    }, [tickets, filterStatus, sortOrder]);

    const handleStatusUpdate = async (id: number, status: string) => {
        try {
            await supportService.updateTicketStatus(id, status);
            // Update local state
            setTickets(prev => prev.map(ticket =>
                ticket.id === id ? { ...ticket, status: status as any } : ticket
            ));
        } catch (err) {
            console.error('Failed to update status', err);
            alert(t('support.admin.updateError', 'Failed to update status'));
        }
    };

    const openMessageModal = (ticket: SupportTicket) => {
        setSelectedTicket(ticket);
        setMessageText('');
        setShowMessageModal(true);
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTicket || !messageText.trim()) return;

        try {
            setSendingMessage(true);
            await messageService.sendMessage({
                recipientId: selectedTicket.userId,
                messageText: messageText.trim()
            });

            alert(t('support.admin.messageSent', 'Message sent successfully'));
            setShowMessageModal(false);
            setMessageText('');
            setSelectedTicket(null);
        } catch (err) {
            console.error('Failed to send message', err);
            // Show specific error message from backend if available
            const errorMessage = (err as any).response?.data?.message || t('common.error', 'An error occurred');
            alert(errorMessage);
        } finally {
            setSendingMessage(false);
        }
    };


    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'OPEN':
                return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit"><Clock size={12} /> {t('support.admin.statusOpen')}</span>;
            case 'RESOLVED':
                return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit"><CheckCircle size={12} /> {t('support.admin.statusResolved')}</span>;
            case 'REJECTED':
                return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit"><XCircle size={12} /> {t('support.admin.statusRejected')}</span>;
            default:
                return status;
        }
    };

    if (loading) return <div className="p-8 text-center">{t('common.loading', 'Loading...')}</div>;

    return (
        <div className="container mx-auto px-4 py-8 relative">
            <h1 className="text-2xl font-bold mb-6">{t('support.admin.title')}</h1>

            {error && <div className="text-red-500 mb-4">{error}</div>}

            {/* Controls */}
            <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between items-start md:items-center bg-white p-4 rounded-lg shadow-sm">
                <div className="flex flex-row flex-wrap gap-4 w-full md:w-auto">
                    {/* Filter */}
                    <div className="flex items-center gap-2">
                        <Filter size={18} className="text-gray-500" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="ALL">{t('support.admin.allStatuses')}</option>
                            <option value="OPEN">{t('support.admin.statusOpen')}</option>
                            <option value="RESOLVED">{t('support.admin.statusResolved')}</option>
                            <option value="REJECTED">{t('support.admin.statusRejected')}</option>
                        </select>
                    </div>

                    {/* Sort */}
                    <div className="flex items-center gap-2">
                        {sortOrder === 'newest' ? <SortDesc size={18} className="text-gray-500" /> : <SortAsc size={18} className="text-gray-500" />}
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="newest">{t('support.admin.newest')}</option>
                            <option value="oldest">{t('support.admin.oldest')}</option>
                        </select>
                    </div>
                </div>
                <div className="text-sm text-gray-500">
                    {filteredTickets.length} {filteredTickets.length === 1 ? 'ticket' : 'tickets'}
                </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('support.admin.id')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('support.admin.user')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('support.admin.subject')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('support.admin.message')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('support.admin.attachment')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('support.admin.status')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('support.admin.actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTickets.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">{t('support.admin.noTickets')}</td>
                            </tr>
                        ) : (
                            filteredTickets.map((ticket) => (
                                <tr key={ticket.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{ticket.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {ticket.user?.profilePicture && (
                                                <img src={ticket.user.profilePicture} alt="" className="h-8 w-8 rounded-full mr-2" />
                                            )}
                                            <div className="text-sm font-medium text-gray-900">{ticket.user?.displayName || t('support.admin.unknownUser')}</div>
                                        </div>
                                        <div className="text-xs text-gray-500">{ticket.user?.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{ticket.subject}</td>
                                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={ticket.message}>
                                        {ticket.message}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {ticket.imagePath ? (
                                            <a href={ticket.imagePath} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900 flex items-center gap-1">
                                                {t('support.admin.view')} <ExternalLink size={12} />
                                            </a>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(ticket.status)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openMessageModal(ticket)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title={t('support.admin.sendMessage')}
                                            >
                                                <MessageCircle size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleStatusUpdate(ticket.id, 'RESOLVED')}
                                                className={`text-green-600 hover:text-green-900 ${ticket.status === 'RESOLVED' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                title={t('support.admin.markResolved')}
                                                disabled={ticket.status === 'RESOLVED'}
                                            >
                                                <CheckCircle size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleStatusUpdate(ticket.id, 'REJECTED')}
                                                className={`text-red-600 hover:text-red-900 ${ticket.status === 'REJECTED' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                title={t('support.admin.reject')}
                                                disabled={ticket.status === 'REJECTED'}
                                            >
                                                <XCircle size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
                {filteredTickets.length === 0 ? (
                    <div className="text-center text-gray-500 py-8 bg-white rounded-lg shadow">{t('support.admin.noTickets')}</div>
                ) : (
                    filteredTickets.map((ticket) => (
                        <div key={ticket.id} className="bg-white shadow rounded-lg p-4 space-y-3">
                            <div className="flex justify-between items-start">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-mono text-gray-500">#{ticket.id}</span>
                                    {getStatusBadge(ticket.status)}
                                </div>
                                <div className="text-xs text-gray-400">
                                    {new Date(ticket.createdAt).toLocaleDateString()}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-900">{ticket.subject}</h3>
                                <div className="flex items-center mt-1">
                                    {ticket.user?.profilePicture && (
                                        <img src={ticket.user.profilePicture} alt="" className="h-5 w-5 rounded-full mr-2" />
                                    )}
                                    <span className="text-sm text-gray-600">{ticket.user?.displayName || t('support.admin.unknownUser')}</span>
                                </div>
                            </div>

                            <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                                {ticket.message}
                            </div>

                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                <div>
                                    {ticket.imagePath && (
                                        <a href={ticket.imagePath} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                                            <ExternalLink size={14} /> {t('support.admin.attachment')}
                                        </a>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openMessageModal(ticket)}
                                        className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200"
                                        title={t('support.admin.sendMessage')}
                                    >
                                        <MessageCircle size={20} />
                                    </button>
                                    <button
                                        onClick={() => handleStatusUpdate(ticket.id, 'RESOLVED')}
                                        className={`p-2 rounded-full ${ticket.status === 'RESOLVED' ? 'bg-green-100 text-green-400' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                                        disabled={ticket.status === 'RESOLVED'}
                                        title={t('support.admin.markResolved')}
                                    >
                                        <CheckCircle size={20} />
                                    </button>
                                    <button
                                        onClick={() => handleStatusUpdate(ticket.id, 'REJECTED')}
                                        className={`p-2 rounded-full ${ticket.status === 'REJECTED' ? 'bg-red-100 text-red-400' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                        disabled={ticket.status === 'REJECTED'}
                                        title={t('support.admin.reject')}
                                    >
                                        <XCircle size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Message Modal */}
            {showMessageModal && selectedTicket && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold">{t('support.admin.sendMessage')}</h3>
                            <button
                                onClick={() => setShowMessageModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="mb-4">
                                <div className="text-sm text-gray-500 mb-1">{t('support.admin.user')}:</div>
                                <div className="font-medium flex items-center">
                                    {selectedTicket.user?.profilePicture && (
                                        <img src={selectedTicket.user.profilePicture} alt="" className="h-6 w-6 rounded-full mr-2" />
                                    )}
                                    {selectedTicket.user?.displayName || t('support.admin.unknownUser')}
                                </div>
                            </div>
                            <form onSubmit={handleSendMessage}>
                                <textarea
                                    className="w-full border rounded-lg p-3 h-32 resize-none focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    placeholder={t('support.admin.messagePlaceholder', 'Type your reply here...')}
                                    value={messageText}
                                    onChange={(e) => setMessageText(e.target.value)}
                                    required
                                ></textarea>
                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowMessageModal(false)}
                                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                    >
                                        {t('support.admin.close', 'Close')}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={sendingMessage || !messageText.trim()}
                                        className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 ${sendingMessage ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {sendingMessage ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                {t('support.sending', 'Sending...')}
                                            </>
                                        ) : (
                                            <>
                                                <Send size={16} />
                                                {t('support.submit', 'Send')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSupportPage;
